import { List, useTable } from "@refinedev/antd";
import { CanAccess, useCan, useNotification } from "@refinedev/core";
import { Button, Popconfirm, Segmented, Space, Table, Tag, Typography } from "antd";
import dayjs from "dayjs";
import { useState } from "react";
import { Link } from "react-router";

import { api } from "../api";
import { CANCELLATION_REASONS, CANCELLATION_REQUEST_STATUS, DATE_TIME_FORMAT } from "../labels";
import type { CancellationRequest } from "../types";

const VIEW_OPTIONS = [
  { value: "pending", label: "Bekleyen" },
  { value: "approved", label: "Onaylanan" },
  { value: "rejected", label: "Reddedilen" },
  { value: "lapsed", label: "Süresi geçen" },
  { value: "all", label: "Tümü" },
];

/** "3 saat 20 dakika sonra" until the lesson starts. */
function startsIn(startsAt: string): string {
  const minutes = dayjs(startsAt).diff(dayjs(), "minute");
  if (minutes <= 0) return "Başladı";
  const hours = Math.floor(minutes / 60);
  return `${hours > 0 ? `${hours} saat ` : ""}${minutes % 60} dakika sonra`;
}

/**
 * /cancellation-requests: tutors' late cancellation requests (permission `lessons.view`;
 * deciding needs `lessons.decideCancellation`). An open request must be answered before the
 * lesson starts; after that it lapses and the lesson takes place.
 */
export function CancellationRequestsPage() {
  const { open } = useNotification();
  const { data: canSeeTutors } = useCan({ resource: "tutors", action: "list" });
  const { data: canSeeStudents } = useCan({ resource: "students", action: "list" });
  const [busy, setBusy] = useState<string | null>(null);
  const { tableProps, tableQuery, filters, setFilters } = useTable<CancellationRequest>({
    resource: "cancellation-requests",
    pagination: { mode: "server", pageSize: 25 },
    filters: { initial: [{ field: "view", operator: "eq", value: "pending" }] },
    syncWithLocation: true,
  });
  const view =
    (filters.find((item) => "field" in item && item.field === "view")?.value as string) ??
    "pending";

  async function decide(request: CancellationRequest, decision: "APPROVE" | "REJECT") {
    setBusy(request.id);
    try {
      await api(`/api/office/cancellation-requests/${request.id}/decision`, {
        method: "POST",
        body: { decision },
      });
      open?.({
        type: "success",
        message:
          decision === "APPROVE"
            ? "Talep onaylandı: ders iptal edildi, öğrencinin saatleri ek kredi olarak iade edildi."
            : "Talep reddedildi: ders planlandığı gibi yapılacak.",
      });
      await tableQuery.refetch();
    } catch (error) {
      open?.({ type: "error", message: error instanceof Error ? error.message : "Hata oluştu." });
    } finally {
      setBusy(null);
    }
  }

  return (
    <List title="İptal talepleri">
      <Typography.Paragraph type="secondary">
        Dersine 8 saatten az kalan eğitmen iptali kendisi yapamaz; talebi burada onaylanır. Onay:
        ders iptal edilir, öğrenci saatlerini süresi dolmayan ek kredi olarak geri alır. Ret veya
        ders başlayana kadar cevap verilmemesi: ders yapılır.
      </Typography.Paragraph>
      <Segmented
        options={VIEW_OPTIONS}
        value={view}
        onChange={(value) => setFilters([{ field: "view", operator: "eq", value }], "replace")}
        style={{ marginBottom: 16 }}
      />
      <Table {...tableProps} rowKey="id" scroll={{ x: "max-content" }}>
        <Table.Column<CancellationRequest>
          title="Ders"
          dataIndex="startsAt"
          render={(startsAt: string, request) => (
            <Space direction="vertical" size={0}>
              <Typography.Text strong>{dayjs(startsAt).format(DATE_TIME_FORMAT)}</Typography.Text>
              <Typography.Text type="secondary">
                {request.subject} · {request.hours} saat
              </Typography.Text>
              {request.requestStatus === "PENDING" ? (
                <Typography.Text type="warning">{startsIn(startsAt)}</Typography.Text>
              ) : null}
            </Space>
          )}
        />
        <Table.Column<CancellationRequest>
          title="Eğitmen"
          dataIndex="tutor"
          render={(tutor: CancellationRequest["tutor"]) =>
            canSeeTutors?.can ? <Link to={`/tutors/${tutor.id}`}>{tutor.name}</Link> : tutor.name
          }
        />
        <Table.Column<CancellationRequest>
          title="Öğrenci"
          dataIndex="student"
          render={(student: string, request) =>
            canSeeStudents?.can ? (
              <Link to={`/students/${request.studentId}`}>{student}</Link>
            ) : (
              student
            )
          }
        />
        <Table.Column<CancellationRequest>
          title="Neden"
          dataIndex="cancellationReason"
          render={(reason: string | null, request) => (
            <Space direction="vertical" size={0}>
              <span>{reason ? (CANCELLATION_REASONS[reason] ?? reason) : "—"}</span>
              {request.cancellationNote ? (
                <Typography.Text type="secondary">{request.cancellationNote}</Typography.Text>
              ) : null}
            </Space>
          )}
        />
        <Table.Column<CancellationRequest>
          title="Talep"
          dataIndex="cancellationRequestedAt"
          render={(value: string | null) => (value ? dayjs(value).format(DATE_TIME_FORMAT) : "—")}
        />
        <Table.Column<CancellationRequest>
          title="Durum"
          dataIndex="requestStatus"
          render={(value: string) => (
            <Tag color={CANCELLATION_REQUEST_STATUS[value]?.color}>
              {CANCELLATION_REQUEST_STATUS[value]?.label}
            </Tag>
          )}
        />
        <Table.Column<CancellationRequest>
          title=""
          key="actions"
          render={(_, request) =>
            request.requestStatus === "PENDING" ? (
              <CanAccess resource="cancellation-requests" action="decide">
                <Space>
                  <Popconfirm
                    title="İptal onaylansın mı?"
                    description="Ders iptal edilir; öğrenci saatlerini ek kredi olarak geri alır."
                    okText="Onayla"
                    cancelText="Vazgeç"
                    onConfirm={() => decide(request, "APPROVE")}
                  >
                    <Button size="small" type="primary" loading={busy === request.id}>
                      Onayla
                    </Button>
                  </Popconfirm>
                  <Popconfirm
                    title="Talep reddedilsin mi?"
                    description="Ders planlandığı gibi yapılır."
                    okText="Reddet"
                    okButtonProps={{ danger: true }}
                    cancelText="Vazgeç"
                    onConfirm={() => decide(request, "REJECT")}
                  >
                    <Button size="small" danger loading={busy === request.id}>
                      Reddet
                    </Button>
                  </Popconfirm>
                </Space>
              </CanAccess>
            ) : null
          }
        />
      </Table>
    </List>
  );
}
